// The vault module is the single source of truth for "is there a key, can we
// sign with it?" — every component that wants to sign a venue request goes
// through here. Decrypted material lives only in this module's closure; it
// never enters Zustand state, never gets serialised, never crosses the WS.
//
// Lifecycle:
//   absent       (no vault record in IDB)
//   locked       (record exists, no in-memory derived key)
//   unlocked     (derived key in memory; venue keys decrypted if present)
//
// On `window.beforeunload` we drop the in-memory keys so a process snapshot
// from another tab can't read them post-navigation.

import { deriveKey, newSalt, open, randomBytes, seal, toHex } from './crypto.js';
import type { SealedBlob } from './crypto.js';
import {
  deleteVault as idbDeleteVault,
  readVault as idbReadVault,
  writeVault as idbWriteVault,
  type PolymarketVaultEntry,
  type VaultRecord,
} from './storage.js';
import { importKalshiKey, signKalshi, type KalshiSignedHeaders } from '../sign/kalshi-browser.js';
import { privateKeyToAccount } from 'viem/accounts';
import type { Account, Hex } from 'viem';
import {
  addressFromPrivateKey,
  signL1Challenge,
  signPolymarketL2,
  type L1Challenge,
  type PolymarketL2Creds,
  type PolymarketL2Headers,
} from '../sign/polymarket-browser.js';

export type VaultPhase = 'absent' | 'locked' | 'unlocked';

interface KalshiMaterial {
  keyId: string;
  cryptoKey: CryptoKey;
}

interface PolymarketMaterial {
  address: `0x${string}`;
  privateKey: string;
  l2: PolymarketL2Creds | null;
}

interface State {
  phase: VaultPhase;
  vaultId: string | null;
  hasKalshi: boolean;
  hasPolymarket: boolean;
  // Mirrors hasPolymarket && polymarketLive.l2 !== null — separate flag
  // because the L1-derived address is independent of L2 availability.
  hasPolymarketL2: boolean;
  polymarketAddress: `0x${string}` | null;
  // In-memory only — never persisted, never read by anything outside this file.
  masterKey: CryptoKey | null;
  salt: Uint8Array | null;
  kalshiSealed: { keyId: string; sealedPem: SealedBlob } | null;
  kalshiLive: KalshiMaterial | null;
  polymarketSealed: PolymarketVaultEntry | null;
  polymarketLive: PolymarketMaterial | null;
}

const state: State = {
  phase: 'absent',
  vaultId: null,
  hasKalshi: false,
  hasPolymarket: false,
  hasPolymarketL2: false,
  polymarketAddress: null,
  masterKey: null,
  salt: null,
  kalshiSealed: null,
  kalshiLive: null,
  polymarketSealed: null,
  polymarketLive: null,
};

type Listener = (snapshot: VaultSnapshot) => void;
const listeners = new Set<Listener>();

export interface VaultSnapshot {
  phase: VaultPhase;
  vaultId: string | null;
  hasKalshi: boolean;
  hasPolymarket: boolean;
  hasPolymarketL2: boolean;
  polymarketAddress: `0x${string}` | null;
}

function snapshot(): VaultSnapshot {
  return {
    phase: state.phase,
    vaultId: state.vaultId,
    hasKalshi: state.hasKalshi,
    hasPolymarket: state.hasPolymarket,
    hasPolymarketL2: state.hasPolymarketL2,
    polymarketAddress: state.polymarketAddress,
  };
}

function emit(): void {
  const s = snapshot();
  for (const l of listeners) l(s);
}

export function subscribeVault(fn: Listener): () => void {
  listeners.add(fn);
  fn(snapshot());
  return () => {
    listeners.delete(fn);
  };
}

export function getSnapshot(): VaultSnapshot {
  return snapshot();
}

function generateVaultId(): string {
  return toHex(randomBytes(16));
}

export async function loadFromDisk(): Promise<void> {
  const rec = await idbReadVault().catch(() => null);
  if (!rec) {
    Object.assign(state, {
      phase: 'absent' as VaultPhase,
      vaultId: null,
      hasKalshi: false,
      hasPolymarket: false,
      hasPolymarketL2: false,
      polymarketAddress: null,
    });
    emit();
    return;
  }
  state.phase = 'locked';
  state.vaultId = rec.vaultId;
  state.salt = rec.salt;
  state.kalshiSealed = rec.kalshi
    ? { keyId: rec.kalshi.keyId, sealedPem: rec.kalshi.sealedPem }
    : null;
  state.hasKalshi = !!rec.kalshi;
  state.polymarketSealed = rec.polymarket ?? null;
  state.hasPolymarket = !!rec.polymarket;
  state.hasPolymarketL2 = !!rec.polymarket?.l2Sealed;
  state.polymarketAddress = rec.polymarket?.address ?? null;
  emit();
}

export async function initialize(passphrase: string): Promise<void> {
  if (state.phase !== 'absent') throw new Error('vault already exists');
  if (passphrase.length < 8) throw new Error('passphrase must be at least 8 characters');
  const salt = newSalt();
  const vaultId = generateVaultId();
  const master = await deriveKey(passphrase, salt);
  const rec: VaultRecord = { vaultId, salt, kalshi: null, polymarket: null };
  await idbWriteVault(rec);
  state.phase = 'unlocked';
  state.vaultId = vaultId;
  state.salt = salt;
  state.masterKey = master;
  state.hasKalshi = false;
  state.kalshiSealed = null;
  state.kalshiLive = null;
  state.hasPolymarket = false;
  state.hasPolymarketL2 = false;
  state.polymarketAddress = null;
  state.polymarketSealed = null;
  state.polymarketLive = null;
  emit();
}

export async function unlock(passphrase: string): Promise<void> {
  if (state.phase === 'unlocked') return;
  if (state.phase === 'absent') throw new Error('vault not initialized');
  if (!state.salt) throw new Error('vault is missing a salt');
  const master = await deriveKey(passphrase, state.salt);
  // Verify by attempting to open the Kalshi blob if we have one. Failing
  // that, try the Polymarket blob. If both are absent, the passphrase is
  // "accepted" by definition.
  let verified = false;
  if (state.kalshiSealed) {
    try {
      const pem = await open(master, state.kalshiSealed.sealedPem);
      const cryptoKey = await importKalshiKey(pem);
      state.kalshiLive = { keyId: state.kalshiSealed.keyId, cryptoKey };
      verified = true;
    } catch {
      throw new Error('wrong passphrase');
    }
  }
  if (state.polymarketSealed) {
    try {
      const pk = await open(master, state.polymarketSealed.pkSealed);
      let l2: PolymarketL2Creds | null = null;
      if (state.polymarketSealed.l2Sealed) {
        const json = await open(master, state.polymarketSealed.l2Sealed);
        l2 = JSON.parse(json) as PolymarketL2Creds;
      }
      state.polymarketLive = { address: state.polymarketSealed.address, privateKey: pk, l2 };
      verified = true;
    } catch {
      if (!verified) throw new Error('wrong passphrase');
      // If Kalshi already decrypted we know the pass is right, so a
      // decode failure here is corruption — surface to the user.
      throw new Error('polymarket vault entry could not be decrypted');
    }
  }
  state.masterKey = master;
  state.phase = 'unlocked';
  emit();
}

export function lock(): void {
  state.masterKey = null;
  state.kalshiLive = null;
  state.polymarketLive = null;
  if (state.salt) state.phase = 'locked';
  else state.phase = 'absent';
  emit();
}

export async function setKalshiKey(keyId: string, pem: string): Promise<void> {
  if (state.phase !== 'unlocked' || !state.masterKey) {
    throw new Error('unlock the vault first');
  }
  if (!keyId.trim()) throw new Error('keyId required');
  const cryptoKey = await importKalshiKey(pem);
  const sealedPem = await seal(state.masterKey, pem);
  if (!state.vaultId || !state.salt) throw new Error('vault state corrupted');
  const rec: VaultRecord = {
    vaultId: state.vaultId,
    salt: state.salt,
    kalshi: { keyId: keyId.trim(), sealedPem },
    polymarket: state.polymarketSealed,
  };
  await idbWriteVault(rec);
  state.kalshiSealed = rec.kalshi;
  state.kalshiLive = { keyId: keyId.trim(), cryptoKey };
  state.hasKalshi = true;
  emit();
}

export async function removeKalshiKey(): Promise<void> {
  if (state.phase !== 'unlocked' || !state.masterKey) {
    throw new Error('unlock the vault first');
  }
  if (!state.vaultId || !state.salt) throw new Error('vault state corrupted');
  const rec: VaultRecord = {
    vaultId: state.vaultId,
    salt: state.salt,
    kalshi: null,
    polymarket: state.polymarketSealed,
  };
  await idbWriteVault(rec);
  state.kalshiSealed = null;
  state.kalshiLive = null;
  state.hasKalshi = false;
  emit();
}

// Persists the EOA private key; clears any cached L2 creds — they belong to
// the previous EOA and must be re-derived. Caller validates the pk shape
// upstream; addressFromPrivateKey throws on a malformed hex string.
export async function setPolymarketKey(privateKey: string): Promise<`0x${string}`> {
  if (state.phase !== 'unlocked' || !state.masterKey) {
    throw new Error('unlock the vault first');
  }
  const address = addressFromPrivateKey(privateKey);
  const pkSealed = await seal(state.masterKey, privateKey);
  if (!state.vaultId || !state.salt) throw new Error('vault state corrupted');
  const polymarket: PolymarketVaultEntry = { address, pkSealed, l2Sealed: null };
  const rec: VaultRecord = {
    vaultId: state.vaultId,
    salt: state.salt,
    kalshi: state.kalshiSealed,
    polymarket,
  };
  await idbWriteVault(rec);
  state.polymarketSealed = polymarket;
  state.polymarketLive = { address, privateKey, l2: null };
  state.hasPolymarket = true;
  state.hasPolymarketL2 = false;
  state.polymarketAddress = address;
  emit();
  return address;
}

export async function setPolymarketL2Creds(creds: PolymarketL2Creds): Promise<void> {
  if (state.phase !== 'unlocked' || !state.masterKey) {
    throw new Error('unlock the vault first');
  }
  if (!state.polymarketSealed || !state.polymarketLive) {
    throw new Error('add a polymarket key before deriving l2');
  }
  if (!state.vaultId || !state.salt) throw new Error('vault state corrupted');
  const l2Sealed = await seal(state.masterKey, JSON.stringify(creds));
  const polymarket: PolymarketVaultEntry = { ...state.polymarketSealed, l2Sealed };
  const rec: VaultRecord = {
    vaultId: state.vaultId,
    salt: state.salt,
    kalshi: state.kalshiSealed,
    polymarket,
  };
  await idbWriteVault(rec);
  state.polymarketSealed = polymarket;
  state.polymarketLive = { ...state.polymarketLive, l2: creds };
  state.hasPolymarketL2 = true;
  emit();
}

export async function removePolymarketKey(): Promise<void> {
  if (state.phase !== 'unlocked' || !state.masterKey) {
    throw new Error('unlock the vault first');
  }
  if (!state.vaultId || !state.salt) throw new Error('vault state corrupted');
  const rec: VaultRecord = {
    vaultId: state.vaultId,
    salt: state.salt,
    kalshi: state.kalshiSealed,
    polymarket: null,
  };
  await idbWriteVault(rec);
  state.polymarketSealed = null;
  state.polymarketLive = null;
  state.hasPolymarket = false;
  state.hasPolymarketL2 = false;
  state.polymarketAddress = null;
  emit();
}

export async function destroyVault(): Promise<void> {
  await idbDeleteVault();
  Object.assign(state, {
    phase: 'absent' as VaultPhase,
    vaultId: null,
    hasKalshi: false,
    hasPolymarket: false,
    hasPolymarketL2: false,
    polymarketAddress: null,
    masterKey: null,
    salt: null,
    kalshiSealed: null,
    kalshiLive: null,
    polymarketSealed: null,
    polymarketLive: null,
  });
  emit();
}

export async function signKalshiRequest(
  method: string,
  path: string,
): Promise<KalshiSignedHeaders | null> {
  if (state.phase !== 'unlocked' || !state.kalshiLive) return null;
  return signKalshi(state.kalshiLive.cryptoKey, state.kalshiLive.keyId, method, path);
}

// Builds an L1 EIP-712 challenge for the EOA stored in the vault. Used once
// per derive flow — the server-returned L2 creds are then cached and used
// for HMAC-signed requests instead.
export async function buildPolymarketL1(): Promise<L1Challenge | null> {
  if (state.phase !== 'unlocked' || !state.polymarketLive) return null;
  return signL1Challenge(state.polymarketLive.privateKey);
}

export async function signPolymarketRequest(
  method: string,
  path: string,
  body = '',
): Promise<PolymarketL2Headers | null> {
  if (state.phase !== 'unlocked' || !state.polymarketLive?.l2) return null;
  return signPolymarketL2(state.polymarketLive.l2, method, path, body);
}

export function getPolymarketAddress(): `0x${string}` | null {
  return state.polymarketAddress;
}

// Runs the supplied callback with a viem Account built from the in-vault
// EOA private key. The pk is never exposed to the caller — only the
// Account object's sign* methods are reachable. Used by the Polygon
// approval flow to sign + send approve / setApprovalForAll txs.
export async function withPolymarketAccount<T>(
  fn: (account: Account) => Promise<T>,
): Promise<T | null> {
  if (state.phase !== 'unlocked' || !state.polymarketLive) return null;
  const account = privateKeyToAccount(state.polymarketLive.privateKey as Hex);
  return fn(account);
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    state.masterKey = null;
    state.kalshiLive = null;
    state.polymarketLive = null;
  });
}
